import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api, formatPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState("loading");
  const [order, setOrder] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setOrder(data.order || null);
          setState("paid");
          clearCart();
          return;
        }
        attempts.current += 1;
        if (attempts.current < 15) setTimeout(poll, 2000);
        else setState("error");
      } catch {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main className="pt-16 min-h-screen flex items-center justify-center px-4" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-lg w-full border border-white/10 bg-coal p-10 text-center"
      >
        {state === "loading" && (
          <>
            <Loader2 size={48} className="mx-auto text-volt animate-spin mb-6" />
            <h1 className="font-syne font-extrabold uppercase tracking-tight text-2xl mb-3">Vérification du paiement…</h1>
            <p className="text-smoke text-sm">Nous confirmons votre commande auprès de Stripe.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-volt mb-6" />
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-blood mb-3">[ Commande confirmée ]</p>
            <h1 className="font-syne font-extrabold uppercase tracking-tight text-3xl mb-4">Merci.</h1>
            <p className="text-smoke text-sm mb-6">
              Votre commande est payée et part en préparation. Un e-mail de confirmation Stripe vous a été envoyé.
            </p>
            {order && (
              <div data-testid="order-receipt" className="border border-white/10 p-5 text-left mb-8 space-y-2">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash mb-3">
                  Commande #{order.id.slice(0, 8).toUpperCase()}
                </p>
                {order.items.map((i) => (
                  <div key={`${i.product_id}-${i.size}`} className="flex justify-between font-mono text-xs text-smoke">
                    <span>{i.name} — T.{i.size} × {i.quantity}</span>
                    <span>{formatPrice(i.price * i.quantity)}</span>
                  </div>
                ))}
                <div className="flex justify-between font-mono text-sm pt-3 border-t border-white/10">
                  <span className="uppercase tracking-[0.15em]">Total payé</span>
                  <span className="text-volt">{formatPrice(order.total)}</span>
                </div>
              </div>
            )}
            <Link
              to="/boutique"
              data-testid="success-back-to-shop"
              className="inline-block bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-white hover:text-ink transition-colors"
            >
              Retour à la boutique
            </Link>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle size={48} className="mx-auto text-blood mb-6" />
            <h1 className="font-syne font-extrabold uppercase tracking-tight text-2xl mb-3">Vérification impossible</h1>
            <p className="text-smoke text-sm mb-8">Le statut du paiement n'a pas pu être confirmé. Si vous avez été débité, contactez-nous.</p>
            <Link to="/boutique" className="inline-block border border-white/20 font-mono text-xs uppercase tracking-[0.2em] px-8 py-4 hover:border-volt hover:text-volt transition-colors">
              Retour à la boutique
            </Link>
          </>
        )}
      </motion.div>
    </main>
  );
}
